import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

export type TestimonialRenderSource = 'GoogleReview' | 'SurveyResponse';

export interface TestimonialRenderRequest {
	templateKey: string;
	templateRevision: number;
	source: TestimonialRenderSource;
	reviewText: string;
	authorName: string;
	rating: number;
	backgroundColor: string;
	foregroundColor: string;
	includeGoogleLogo: boolean;
	includeStars: boolean;
	includeAuthor: boolean;
	backgroundImageId: string | null;
	layout?: Partial<Api.IGoogleReviewPostLayout> | null;
}

export const STALE_TEMPLATE = 409;
export const RENDER_BUSY = 429;

export const useRenderTestimonialMutation = ({
	impersonationContext,
	onError,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) => {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: (request: TestimonialRenderRequest) =>
			userSession.webServiceHelper.callAsync<Api.IFileAttachment>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'googleBusiness/testimonial/render',
				}),
				'POST',
				request
			),
		onError,
	});
};

export const describeRenderFailure = (error: Api.IOperationResultNoValue): string => {
	switch (error?.systemCode) {
		case STALE_TEMPLATE:
			return 'The testimonial design was updated while you were editing. Reloading it now — please check the preview and approve again.';
		case RENDER_BUSY:
			return 'The image renderer is busy. Please try again in a moment.';
		case 400:
		case 404:
			return error.systemMessage || 'That testimonial could not be rendered. Please check your settings and try again.';
		default:
			return 'Unable to generate the testimonial image. Please try again.';
	}
};
